import { Box, Button, Grid, Paper, Snackbar, Step, StepContent, Stepper, Typography, useMediaQuery, useTheme, Alert, AlertTitle } from '@mui/material';
import { useFormLookups, ChosenThemeActionContext, useFormValidate, FormSubmitButton,  FormsFeatureContext, IValidationError, useFormMetadata } from '@ngt/forms';
import { getLookupIdFromValue } from '@/utils/lookupUtils';
import { ValidateOn, IFormSubmit, useFormActions } from '@ngt/forms-core';
import React, { useCallback, useRef, useState } from 'react';
import * as Dtos from '../../api/dtos';
import { FC, useContext, useEffect } from 'react';
import RegistrationSteps from '../../components/registration/RegistrationSteps';
import NextButton from '../../components/NextButton';
import StepLabelCheck from '../StepLabelCheck';
import { RenderRegistrationCheck, RenderPackageCheck } from './RenderDetailsCheck';
import { useAuthenticatedUser } from '@/hooks/useAuthenticatedUser';
import useMemberDetails from '@/hooks/useMemberDetails';
import { immerable } from 'immer';
import RegistrationNavigation from './RegistrationNavigation';
import Invoice from './Invoice';
import {useReactToPrint} from 'react-to-print';
import { ASMHomePage, REGISTRATION_FORM_DEFINITION_ID } from '@/constants/constants';
import FormError from '../common/FormError';
import StepUpdater from '../StepUpdater';
import { FieldContext } from '@/contexts/FieldContext';
import { FieldActionContext } from '@/contexts/FieldActionContext';
import { Registration, RegistrationSession, ResponseStatus } from '../../api/dtos';
import getCreditCardErrorMessage from '@/utils/getCreditCardErrorMessage';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faInfoCircle, faTimesCircle } from '@fortawesome/pro-solid-svg-icons';
import { useScopedField } from "@ngt/forms-core";
import PackageProvider from './packages/PackageProvider';
import { AdminContext } from '@/contexts/AdminContext';
import RegistrationCostUpdater from '../RegistrationCostUpdater';
import FormPayment from '../payment/FormPayment';
import PrevButton from '../buttons/PrevButton';
import StandaloneForm from '../common/StandaloneForm';
import { DateTime } from 'luxon';
import { FORM_DEBUG } from '@/formSettings';
import { useValidationErrorCodes } from '@/hooks/validationErrorCodes';
import RegistrationTypeUpdater from '../shared/RegistrationTypeUpdater';

const subscription = {
    value: true
};

const newFormData: Partial<Dtos.Registration> = {};

export interface IRegistrationFormProps {
    sponsorMode?: boolean;
}

const SponsorUpdater: React.FunctionComponent<IRegistrationFormProps> = ({sponsorMode}) => {
    const {
        context: {
            setValue: setSponsor
        }
    } = useScopedField<boolean>('sponsor', subscription);
    
    setSponsor(sponsorMode);

    return null;
}

const FreeSponsorPositionChecker = () => {
    const validationErrorCodes = useValidationErrorCodes();
    const {
        context: {
            setValue: setSponsorId
        }
    } = useScopedField<boolean>('sponsorId', subscription);

    const {
        context: {
            setValue: setPackageId
        }
    } = useScopedField<number>('packageId', subscription);

    const {
        context: {
            setValue: setSessions
        }
    } = useScopedField<RegistrationSession[]>('sessions', subscription);

    const actions = useFormActions<Registration, IValidationError>();

    let outOfFreeRego;

    if (actions.getErrors().packageId)
    {
        if (actions.getErrors().packageId.find(error => error.code == validationErrorCodes.freeSponsorRegistrationsExhausted) != undefined)
        {
            outOfFreeRego = true;

            setSponsorId(null);
            setPackageId(null);
            setSessions(null);
        }
    }

    return (
            outOfFreeRego ?
                <Snackbar
                    open={true}
                    autoHideDuration={8000}
                    anchorOrigin={{vertical:'bottom', horizontal:'center'}}
                >
                    <Alert
                        icon={<FontAwesomeIcon icon={faTimesCircle} />}
                        severity="error"
                        sx={{
                            alignItems: 'center',
                            width: '100%'
                        }}
                    >
                        <AlertTitle>Cost Of Packages Have Changed!</AlertTitle> 
                        Unfortunately this sponsors free registrations have been used up! Please return to packages to continue.
                    </Alert>
                </Snackbar>
                :
                null
    );
}


const PaymentUpdater: FC = () => {
    
    const {state: { value: cost }} = useScopedField<number>("cost", { value: true });
    const {state: { value: payments }, context: { setValue: setPayments }} = useScopedField<Dtos.Payment[]>("payments", { value: true });

    useEffect(() => {
        if (cost === 0) {
            setPayments(undefined);
        }
        else {
            if (payments === undefined) {
                setPayments([]);
            }
        }
    }, [cost, setPayments, payments])

    return null;
}


const RegistrationForm: React.FunctionComponent<IRegistrationFormProps> = ({
    sponsorMode
}) => {
    //const {state: { value: cost }} = useScopedField<number>("cost", { value: true });
    const setChosenTheme = useContext(ChosenThemeActionContext);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down(650));

    const formMetadata = useFormMetadata(REGISTRATION_FORM_DEFINITION_ID);
    const {data:lookups} = useFormLookups(formMetadata, { revalidateOnFocus: false, refreshWhenOffline: false, });
    const validate: any = useFormValidate(formMetadata);
    useEffect(() => {
        setChosenTheme('light');
    }, [])
    const {serviceStackClient} = useContext(FormsFeatureContext);

    const [error, setError] = React.useState<any>();
    const [hasError, setHasError] = React.useState<boolean>(false);
    const [done, setFormDone] = React.useState<Dtos.Registration>();
    const [activeStep, setActiveStep] = React.useState(0);
    const [completed, setCompleted] = React.useState<{
        [k: number]: boolean;
      }>({}); 

    const [openPaymentModal, setOpenPaymentModal] = React.useState(false);
    const [, setFormBeforePayment] = React.useState<Dtos.Registration>();
  
    const totalSteps = useCallback(() => {
        return RegistrationSteps.length;
    }, []);

    const completedSteps = useCallback(() => {
        const keys = Object.keys(completed);

        for (let i = 0; i < keys.length; i++) {
            if (!completed[+keys[i]]) {
                return i;
            } 
        }

        return keys.length;
    }, [completed]);

    const stepLabelOnClick = useCallback((index: number) => {
        const finishedSteps = completedSteps();

        if (finishedSteps >= index) {
            setActiveStep(index);
        }
        else {
            setActiveStep(finishedSteps);
        }
    }, [completedSteps, setActiveStep]);

    const isLastStep = useCallback(() => {
        return activeStep === totalSteps() - 1;
    }, [totalSteps, activeStep]);

    const allStepsCompleted = useCallback(() => {
        return completedSteps() === totalSteps();
    }, [completedSteps, totalSteps]);

    const handleNext = useCallback(() => {
        // const {fields} = useFormState({fields: true});
        const lastStep = isLastStep();
        const allStepsComplete = allStepsCompleted()

        const newActiveStep =
            lastStep && !allStepsComplete ? // It's the last step, but not all steps have been completed,
                // find the first step that has been completed
                RegistrationSteps.findIndex((_step, i) => !(i in completed))
            : activeStep + 1;

        const finishedSteps = completedSteps();

        if (finishedSteps >= newActiveStep) {
            setActiveStep(newActiveStep);
        }
        else {
            setActiveStep(finishedSteps);
        }
    }, [isLastStep, allStepsCompleted, completedSteps, completed, activeStep, setActiveStep]);

    const handleBack = () => { setActiveStep(c => c - 1)};

    const handleSnackClose = (_event?: React.SyntheticEvent | Event, reason?: string) => {
        if (reason === 'clickaway') {
            return;
        }
    
        setHasError(false);
    };

    const paymentStateRef = useRef<{ paymentComplete: (clientReferenceId?: string) => void, paymentFailure: (error: any) => void }>();
    
    const awaitPayment = useCallback(() => {
        return new Promise<string | undefined>((resolve, reject) => {
            paymentStateRef.current = {
                paymentComplete: (clientReferenceId) => {
                    resolve(clientReferenceId);
                    paymentStateRef.current = undefined;
                },
                paymentFailure: (error) => {
                    reject(error);
                    paymentStateRef.current = undefined;
                }
            }
        });
    }, [paymentStateRef]);

    const handleStripePaymentComplete = useCallback((clientReferenceId?: string) => {

        setOpenPaymentModal(false);

        if (paymentStateRef.current) {
            if (clientReferenceId) {
                paymentStateRef.current.paymentComplete(clientReferenceId);
            }
            else {
                paymentStateRef.current.paymentFailure("clientReferenceId not found!");
            }
        }

        // verify it somehow

        // if it is successful then save the form and add the payment into the database
        

        
    }, [setOpenPaymentModal, paymentStateRef])


    const onSubmit:IFormSubmit<Dtos.Registration, IValidationError> = async (formState, formActions) => {
        if(!formState.value)
            return;
        
        // instead of sving the actual registration, we save the form s

        // store for later ill make another thing where it will add the commented stuff and post the registration

        const request = new Dtos.PostRegistrationSave({
            registration: {
                ...formState.value,
                payments: [
                    ...(formState.value.payments ?? [])
                ] 
            }
        });

        if (formState.value?.payments?.[0]?.paymentType === (Dtos.AdminPaymentType.Card as number)) 
        {
            setFormBeforePayment(formState.value);
            setOpenPaymentModal(true);

            try {
                const clientReferenceId = await awaitPayment();

                if (!clientReferenceId) {
                    // cancelled.
                    return;
                } 

                request.registration!.payments = [
                    new Dtos.Payment({
                        amountPaid: formState.value.cost,
                        receiptNumber: clientReferenceId,
                        paymentType: Dtos.AdminPaymentType.Card,
                        attemptNumber: 1,
                        paymentDate: DateTime.now().toISO({ includeOffset: false }),
                        paymentSuccess: true
                    })
                ];
            }
            catch (error) {
                // do something cause stuff done broke.
                return;
            }
        }

        try {
            const response = await serviceStackClient.post(request);

            if (response.payment?.paymentSuccess !== false) {
                setFormDone(response.registration);
            }
            else {
                formActions.setValue(response.registration);
                setHasError(true); 
                setError({ responseStatus: {
                    message: (
                        <>
                            {response.payment.paymentStatus ? getCreditCardErrorMessage(response.payment.paymentStatus) :  "An error occured while trying to save the form"}
                            {
                                response.payment.paymentMessage && (
                                    <Box sx={{pt: 2}}>
                                        <strong>Bank Message:</strong> {response.payment.paymentMessage}
                                    </Box>
                                )
                            }
                        </>
                    ) as any
                } as ResponseStatus});
            }
        }
        catch(err:any) {
            setHasError(true); 
            setError(err);
        }
    }
    
    const invoiceRef = useRef<React.ReactInstance>();

    const handlePrint = useReactToPrint({
        content: () => invoiceRef.current ?? null
    });

    const authUser = useAuthenticatedUser<Dtos.UserDto>();
    const userDetails = useMemberDetails(authUser?.firstName, authUser?.lastName);

    // create one of these which sets a value in the user details something like 
    const initialValue = React.useMemo(() => {

        if (!!userDetails) {
            const stateId = getLookupIdFromValue(lookups, 'state', userDetails.State);

            return {
                [immerable]: true,
                attendanceType: 1,
                payments: [{}],
                title: userDetails.Title,
                firstName: userDetails.FirstName,
                lastName: userDetails.LastName,
                address: userDetails.Address,
                suburb: userDetails.Suburb,
                postcode: userDetails.Postcode,
                state: stateId,
                country: userDetails.Country,
                phoneNumber: userDetails.Phone,
                email: userDetails.Email,
                speciality: userDetails.Speciality,
                specialitySpec: userDetails.SpecialitySpec,
                institution: userDetails.Institution,
                ...newFormData
            } as Dtos.Registration;
        }

        return {
            payments: [{}],
            attendanceType: 1,
            ...newFormData
        } as Dtos.Registration;
    }, [userDetails, lookups]);

    return (
            <StandaloneForm
                initialValue={initialValue}
                debug={FORM_DEBUG}
                lookups={lookups}
                onValidate={validate}
                validateOn={ValidateOn.onChange}
                validateOnReset={true}
                onSubmit={onSubmit}
            >
                <AdminContext.Provider value={false}>
                    <PackageProvider>
                        <StepUpdater setCompleted={setCompleted} />
    
                        <SponsorUpdater sponsorMode={sponsorMode} />

                        <PaymentUpdater />
    
                        <RegistrationCostUpdater />
                        {/* <CreditCardCostUpdater /> */}
                        <RegistrationNavigation/>
                        <Box className="box">
                        {                     
                            done ?
                            <Box sx={{
                                pb: '50px'
                            }}>
                                <Grid 
                                    container
                                    justifyContent="space-between"
                                    spacing={2}
                                    sx={{
                                        px:{
                                            xs: '10%',
                                            sm: '9%',
                                            md: '20%',
                                            lg: '28%'
                                        },
                                        mt:{
                                            xs: '50px',
                                            sm: '50px'
                                        }
                                    }}>
                                    <Grid item xs={12}>
                                        <Typography
                                            variant="h2"
                                        >
                                            Success!
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={12}>
                                        <Typography ml={2}>A copy of the invoice has been emailed to the address provided</Typography>
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                        <Button
                                            variant="contained"
                                            color="secondary"
                                            size="large"
                                            sx={{
                                                width: {
                                                    xs: '100%',
                                                    md: '100%'
                                                },
                                                minWidth: '160px',
                                                border: 'solid',
                                                borderColor: 'secondary.main',
                                                borderRadius: '20px',
                                                '&:hover': {
                                                    backgroundColor: 'primary.main',
                                                    borderColor: 'primary.main'
                                                }
                                            }}
                                            onClick={()=>handlePrint()}
                                        >
                                            <Typography
                                                variant="body2"
                                            >   
                                                Print Invoice
                                            </Typography>
                                        </Button>
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                        <Button
                                            variant="contained"
                                            color="secondary"
                                            size="large"
                                            sx={{
                                                width: '100%',
                                                minWidth: '150px',
                                                border: 'solid',
                                                borderColor: 'secondary.main',
                                                borderRadius: '20px',
                                                '&:hover': {
                                                    backgroundColor: 'primary.main',
                                                    borderColor: 'primary.main'
                                                }
                                            }}
                                            href={ASMHomePage}
                                        >
                                            <Typography
                                                variant="body2"
                                            >   
                                                Return Home
                                            </Typography>
                                        </Button>
                                    </Grid>
                                </Grid>
                                {!isMobile && ( 
                                <Invoice
                                    ref={invoiceRef}
                                    key={'invoice'+ done.id}
                                    registration={done}
                                />)}
                            </Box>
                            :
                            <>
                            
                            <FreeSponsorPositionChecker />
    
                                {
                                    sponsorMode?
                                        <Alert
                                            icon={<FontAwesomeIcon icon={faInfoCircle} />}
                                            severity="warning"
                                            sx={{
                                                alignItems: 'center'
                                            }}
                                            style={{padding: 25, fontSize: 16}}
                                        >
                                            Pricing available in <strong>Step 3: Package Selection & Pricing</strong>, pricing is based on your membership status and sponsor benefits.
                                        </Alert>
                                    :
                                        <Alert
                                            icon={<FontAwesomeIcon icon={faInfoCircle} />}
                                            severity="warning"
                                            sx={{
                                                alignItems: 'center'
                                            }}
                                            style={{padding: 25, fontSize: 16}}
                                        >
                                            Pricing available in <strong>Step 3: Package Selection & Pricing</strong>, pricing is based on your membership status and attendance type.
                                        </Alert>
                                }
    
    
                                <RegistrationTypeUpdater />
                                <Stepper nonLinear sx={{marginTop: "20px", marginBottom: "20px", ml: { xs: 1, md: 0}, '& .MuiStepContent-root': { pl: 1 }}} activeStep={activeStep} orientation="vertical">
                                    {RegistrationSteps.map(({label, Inputs}, index) => (
                                            <Step key={label} completed={completed[index]}>
                                                <StepLabelCheck 
                                                    onClick={() => stepLabelOnClick(index)}
                                                    label={label}
                                                    index={activeStep}
                                                    setComplete={setCompleted}
                                                />
                                                <StepContent>
                                                    {
                                                        activeStep == 4 && (
                                                            <>
                                                                <Paper
                                                                    sx={{
                                                                        p: 4, 
                                                                        mt: { xs: 1, md: 3 }
                                                                    }}
                                                                >
                                                                    <Typography
                                                                        variant="body1"
                                                                        sx={{
                                                                            mb: 2
                                                                        }}
                                                                    >
                                                                        Please check the information you have entered carefully. 
                                                                        If you need to make changes to the information you have entered please click on 'Update' below.
                                                                    </Typography>
                                                                    <Typography
                                                                        variant="body1"
                                                                    >
                                                                        Once you have checked your registration information please choose a payment option by clicking the relevant payment button. 
                                                                        A tax invoice will be generated after your payment has been successfully processed.
                                                                    </Typography>
                                                                </Paper>
                                                                <Grid 
                                                                    container
                                                                    sx={{
                                                                        mt: 3
                                                                    }}
                                                                >
                                                                    <Grid 
                                                                        item
                                                                        xs={12}
                                                                        sm={7}
                                                                        md={9}
                                                                        sx={{
                                                                            pb:{
                                                                                xs: 1,
                                                                                md: 1
                                                                            },
                                                                            alignSelf: 'center'
                                                                        }}
                                                                    >
                                                                        <Typography
                                                                            variant="h3"
                                                                            color='secondary'
                                                                        >
                                                                            Registration Information
                                                                        </Typography>
                                                                    </Grid>
                                                                    <Grid 
                                                                        item
                                                                        xs={12}
                                                                        sm={5}
                                                                        md={3}
                                                                        sx={{
                                                                            textAlign: 'right'
                                                                        }}
                                                                    >
                                                                        <Button
                                                                            variant="contained"
                                                                            color="secondary"
                                                                            size="large"
                                                                            sx={{
                                                                                width:{
                                                                                    xs: '100%',
                                                                                    sm: 'fit-content',
                                                                                    md: 'fit-content'
                                                                                },
                                                                                minWidth: '160px',
                                                                                border: 'solid',
                                                                                borderColor: 'secondary.main',
                                                                                borderRadius: '20px',
                                                                                '&:hover': {
                                                                                    backgroundColor: 'primary.main',
                                                                                    borderColor: 'primary.main'
                                                                                }
                                                                            }}
                                                                            onClick={() => setActiveStep(3)}
                                                                        >
                                                                            <Typography
                                                                                variant="body2"
                                                                            >   
                                                                                Update
                                                                            </Typography>
                                                                        </Button>
                                                                    </Grid>
                                                                    <Grid item xs={12}>
                                                                        <RenderRegistrationCheck/>
                                                                    </Grid>
                                                                </Grid>
                                                                <Grid  
                                                                    container
                                                                    sx={{
                                                                        mt: 3
                                                                    }}
                                                                >
                                                                    <Grid 
                                                                        item
                                                                        xs={12}
                                                                        sm={7}
                                                                        md={9}
                                                                        sx={{
                                                                            pb:{
                                                                                xs: 1,
                                                                                md: 1
                                                                            },
                                                                            alignSelf: 'center'
                                                                        }}
                                                                    >
                                                                        <Typography
                                                                            variant="h3"
                                                                            color='secondary'
                                                                        >
                                                                            Package / Sessions
                                                                        </Typography>
                                                                    </Grid>
                                                                    <Grid 
                                                                        item
                                                                        xs={12}
                                                                        sm={5}
                                                                        md={3}
                                                                        sx={{
                                                                            textAlign: 'right'
                                                                        }}
                                                                    >
                                                                        <Button
                                                                            variant="contained"
                                                                            color="secondary"
                                                                            size="large"
                                                                            sx={{
                                                                                width:{
                                                                                    xs: '100%',
                                                                                    sm: 'fit-content',
                                                                                    md: 'fit-content'
                                                                                },
                                                                                minWidth: '160px',
                                                                                border: 'solid',
                                                                                borderColor: 'secondary.main',
                                                                                borderRadius: '20px',
                                                                                '&:hover': {
                                                                                    backgroundColor: 'primary.main',
                                                                                    borderColor: 'primary.main'
                                                                                }
                                                                            }}
                                                                            onClick={() => setActiveStep(2)}
                                                                        >
                                                                            <Typography
                                                                                variant="body2"
                                                                            >   
                                                                                Update
                                                                            </Typography>
                                                                        </Button>
                                                                    </Grid>
                                                                </Grid>
                                                                <RenderPackageCheck/>
                                                            </>
                                                        )
                                                    }
                                                    <Inputs />
                                                    {
                                                        activeStep === 0 ?
                                                            <NextButton 
                                                                onClick={handleNext} 
                                                                index={activeStep}
                                                                setComplete={setCompleted}
                                                            />
                                                        : activeStep === 4 ?
                                                        <Box>
                                                            <FormSubmitButton variant="contained"
                                                                sx={{
                                                                    float: "right",
                                                                    marginTop:"20px",
                                                                }}>
                                                                Submit
                                                            </FormSubmitButton>
                                                            <PrevButton onClick={handleBack} />
                                                            <FormError error={error} hasError={hasError} handleClose={handleSnackClose} index={0}/>
                                                        </Box>
                                                        :
                                                        <Box>
                                                            <NextButton 
                                                                onClick={handleNext} 
                                                                index={activeStep}
                                                                setComplete={setCompleted}
                                                            />
                                                            <PrevButton onClick={handleBack} />
                                                        
                                                        </Box>
                                                    }
                                                </StepContent>
                                            </Step>
                                    ))}
                                </Stepper>
                            </>
                            }
                            </Box>                        
                        </PackageProvider>
                    </AdminContext.Provider>
                    {
                        openPaymentModal &&(
                            <FormPayment 
                                handleStripePaymentComplete={handleStripePaymentComplete} 
                                handlePaymentCancel={() => {
                                    setOpenPaymentModal(false);
                                    
                                    paymentStateRef.current?.paymentComplete(undefined);
                                }}
                            />
                        )
                    }
                </StandaloneForm>
    );
}

const WrappedForm: React.FunctionComponent<IRegistrationFormProps> = ({sponsorMode}) => {
    const [fields, setFields] = useState<Record<number,string[]>>({});

    return (
        <FieldContext.Provider value={fields}>
            <FieldActionContext.Provider value={setFields}>
                <RegistrationForm sponsorMode={sponsorMode} />
            </FieldActionContext.Provider>
        </FieldContext.Provider>
    );
}

export default WrappedForm;